import React, { useState } from 'react';

/**
 * UnifiedLandlordFlow — CHANGE 2 (2026-05-29).
 *
 * A single-screen Add-Landlord wizard that replaces the multi-tab flow.
 * Sections appear vertically as the agent progresses:
 *   A. Landlord details
 *   B. First property (with Available-to-Let | Currently-Let branch)
 *   C. "Add another property" button to repeat Section B
 *
 * Props:
 *   apiBase:   REACT_APP_BACKEND_URL
 *   onClose:   () => void      Called when the agent finishes.
 *   onSaved:   () => void      Called after every successful save so the
 *                              parent can refresh its landlord list.
 */

const BTN_PRIMARY = {
  background: '#0073EA', color: '#fff', border: 'none',
  padding: '0.55rem 1.1rem', borderRadius: 6,
  fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
};
const BTN_GHOST = {
  background: '#fff', color: '#0f172a',
  border: '1px solid #cbd5e1',
  padding: '0.55rem 1.1rem', borderRadius: 6,
  fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
};
const SECTION = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: '1.25rem',
  marginBottom: '1.25rem',
};
const SECTION_TITLE = {
  margin: 0, marginBottom: '0.85rem',
  fontSize: '1.05rem', color: '#0f172a',
};
const FIELD = { display: 'flex', flexDirection: 'column', gap: '0.3rem' };
const LBL = { fontSize: '0.82rem', color: '#475569', fontWeight: 500 };
const INPUT = {
  padding: '0.5rem 0.7rem', borderRadius: 6,
  border: '1px solid #cbd5e1', fontSize: '0.9rem',
};

// ---------------------------------------------------------------------------
// SECTION A — Landlord details
// ---------------------------------------------------------------------------
function SectionA({ value, onChange, onSave, saving, err }) {
  const v = value;
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  return (
    <div style={SECTION} data-testid="ulf-section-a">
      <h3 style={SECTION_TITLE}>A. Landlord Details</h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
        <div style={FIELD}>
          <span style={LBL}>Landlord type *</span>
          <select value={v.landlord_type}
                  onChange={set('landlord_type')}
                  style={INPUT}
                  data-testid="ulf-ll-type">
            <option value="individual">Individual</option>
            <option value="company">Limited Company</option>
          </select>
        </div>

        {v.landlord_type === 'company' ? (
          <>
            <div style={FIELD}>
              <span style={LBL}>Company name *</span>
              <input style={INPUT} value={v.company_name || ''}
                     onChange={set('company_name')}
                     data-testid="ulf-ll-company" />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Director name *</span>
              <input style={INPUT} value={v.director_name || ''}
                     onChange={(e) => onChange({
                       ...v,
                       director_name: e.target.value,
                       full_name: e.target.value,
                     })}
                     data-testid="ulf-ll-director" />
            </div>
          </>
        ) : (
          <div style={FIELD}>
            <span style={LBL}>Full name *</span>
            <input style={INPUT} value={v.full_name || ''}
                   onChange={set('full_name')}
                   data-testid="ulf-ll-name" />
          </div>
        )}

        <div style={FIELD}>
          <span style={LBL}>Correspondence address line 1 *</span>
          <input style={INPUT} value={v.address_line1}
                 onChange={set('address_line1')}
                 data-testid="ulf-ll-addr1" />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Address line 2</span>
          <input style={INPUT} value={v.address_line2}
                 onChange={set('address_line2')}
                 data-testid="ulf-ll-addr2" />
        </div>
        <div style={FIELD}>
          <span style={LBL}>City *</span>
          <input style={INPUT} value={v.city}
                 onChange={set('city')}
                 data-testid="ulf-ll-city" />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Postcode *</span>
          <input style={INPUT} value={v.postcode}
                 onChange={set('postcode')}
                 placeholder="SW1A 1AA"
                 data-testid="ulf-ll-postcode" />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Email *</span>
          <input style={INPUT} type="email" value={v.email}
                 onChange={set('email')}
                 data-testid="ulf-ll-email" />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Phone *</span>
          <input style={INPUT} value={v.phone_number}
                 onChange={set('phone_number')}
                 placeholder="+447911123456"
                 data-testid="ulf-ll-phone" />
        </div>
      </div>

      {err && <div role="alert"
                   data-testid="ulf-a-error"
                   style={{ marginTop: '0.75rem', color: '#b91c1c',
                            fontSize: '0.85rem' }}>{err}</div>}

      <div style={{ marginTop: '1rem', display: 'flex',
                    justifyContent: 'flex-end' }}>
        <button onClick={onSave}
                disabled={saving}
                style={BTN_PRIMARY}
                data-testid="ulf-save-landlord">
          {saving ? 'Saving…' : 'Save Landlord & Continue'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION B — Property + status branch
// Exported separately so the Edit-Landlord form can mount it too — that
// way agents can add new Let/Available properties without having to
// recreate the landlord.
// ---------------------------------------------------------------------------
export function LandlordAddPropertyCard({ index = 0, landlordId, apiBase, onSaved }) {
  const [prop, setProp] = useState({
    address_line1: '', address_line2: '', city: '', postcode: '',
    property_type: 'flat', bedrooms: 1,
    // 2026-05-30 — Licensing question per Compliance Scanner item 9.
    // Captured on every property regardless of status so the scanner
    // can flag licensing gaps before a tenancy starts.
    licensing_required: false,
    licensing_type: '',           // 'mandatory_hmo' | 'selective' | 'additional'
    licence_number: '',
    licence_expiry: '',
  });
  const [status, setStatus] = useState(null);  // 'available' | 'occupied'
  const [available, setAvailable] = useState({
    asking_rent: '', available_from: '', listing_description: '',
  });
  const [tenant, setTenant] = useState({
    first_name: '', last_name: '', email: '', phone: '',
  });
  const [tenancy, setTenancy] = useState({
    start_date: '', rent_amount: '', rent_frequency: 'monthly',
    rent_due_day: 1, deposit_amount: '', deposit_scheme: 'DPS',
    tenancy_agreement_url: null,
  });
  const [agreementName, setAgreementName] = useState(null);
  const [complianceFiles, setComplianceFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);

  const setP = (k) => (e) => setProp(p => ({ ...p, [k]: e.target.value }));
  const setA = (k) => (e) => setAvailable(a => ({ ...a, [k]: e.target.value }));
  const setT = (k) => (e) => setTenant(t => ({ ...t, [k]: e.target.value }));
  const setTen = (k) => (e) => setTenancy(t => ({ ...t, [k]: e.target.value }));

  const handleAgreementUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAgreementName(f.name);
    // Convert to data: URL so we can persist it as tenancy_agreement_url.
    const reader = new FileReader();
    reader.onload = () => {
      setTenancy(t => ({ ...t, tenancy_agreement_url: reader.result }));
    };
    reader.readAsDataURL(f);
  };

  const handleComplianceUpload = (e) => {
    const files = Array.from(e.target.files || []);
    setComplianceFiles(prev => [...prev, ...files]);
  };

  const save = async () => {
    setErr(null);
    if (!prop.address_line1.trim() || !prop.city.trim() || !prop.postcode.trim()) {
      setErr('Property address, city and postcode are required.'); return;
    }
    if (!status) { setErr('Select Currently Let or Available to Let.'); return; }

    const payload = {
      property: {
        address_line1: prop.address_line1.trim(),
        address_line2: prop.address_line2.trim(),
        city: prop.city.trim(),
        postcode: prop.postcode.trim(),
        property_type: prop.property_type,
        bedrooms: parseInt(prop.bedrooms, 10) || null,
        landlord_id: landlordId,
        // 2026-05-30 — Licensing
        licensing_required: !!prop.licensing_required,
        licensing_type: prop.licensing_type || null,
        licence_number: prop.licence_number || null,
        licence_expiry: prop.licence_expiry || null,
      },
      status,
    };
    if (status === 'available') {
      payload.property.asking_rent = available.asking_rent
        ? parseFloat(available.asking_rent) : null;
      payload.property.available_from = available.available_from || null;
      payload.property.listing_description = available.listing_description || null;
    } else {
      if (!tenant.first_name || !tenant.last_name) {
        setErr('Tenant first and last name are required.'); return;
      }
      if (!tenancy.start_date || !tenancy.rent_amount) {
        setErr('Tenancy start date and rent amount are required.'); return;
      }
      payload.tenant = {
        first_name: tenant.first_name.trim(),
        last_name: tenant.last_name.trim(),
        email: tenant.email.trim() || null,
        phone: tenant.phone.trim() || null,
      };
      payload.tenancy = {
        start_date: tenancy.start_date,
        rent_amount: parseFloat(tenancy.rent_amount),
        rent_frequency: tenancy.rent_frequency,
        rent_due_day: parseInt(tenancy.rent_due_day, 10) || 1,
        deposit_amount: parseFloat(tenancy.deposit_amount || 0),
        deposit_scheme: tenancy.deposit_scheme,
        tenancy_agreement_url: tenancy.tenancy_agreement_url,
      };
    }

    setSaving(true);
    try {
      const res = await fetch(
        `${apiBase}/api/landlords/${landlordId}/properties/unified`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setErr(data.detail || 'Could not save property.');
        return;
      }

      // Upload compliance files (if any) by re-using the existing compliance
      // upload endpoint. Each file is auto-typed by AI on the backend.
      for (const f of complianceFiles) {
        try {
          const fd = new FormData();
          fd.append('property_id', data.property_id);
          fd.append('file', f);
          await fetch(`${apiBase}/api/compliance/upload-auto`, {
            method: 'POST', credentials: 'include', body: fd,
          });
        } catch (_e) { /* non-fatal */ }
      }

      setSaved(true);
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div style={{ ...SECTION, borderColor: '#a7f3d0',
                    background: '#f0fdf4' }}
           data-testid={`ulf-section-b-${index}-saved`}>
        <div style={{ color: '#065f46', fontWeight: 600 }}>
          Property {index + 1} saved — {prop.address_line1}, {prop.postcode}{' '}
          ({status === 'available' ? 'Available to Let' : 'Currently Let'}).
        </div>
      </div>
    );
  }

  return (
    <div style={SECTION} data-testid={`ulf-section-b-${index}`}>
      <h3 style={SECTION_TITLE}>
        B{index > 0 ? `${index + 1}` : ''}. Add Property
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
        <div style={FIELD}>
          <span style={LBL}>Address line 1 *</span>
          <input style={INPUT} value={prop.address_line1}
                 onChange={setP('address_line1')}
                 data-testid={`ulf-prop-addr1-${index}`} />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Address line 2</span>
          <input style={INPUT} value={prop.address_line2}
                 onChange={setP('address_line2')}
                 data-testid={`ulf-prop-addr2-${index}`} />
        </div>
        <div style={FIELD}>
          <span style={LBL}>City *</span>
          <input style={INPUT} value={prop.city}
                 onChange={setP('city')}
                 data-testid={`ulf-prop-city-${index}`} />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Postcode *</span>
          <input style={INPUT} value={prop.postcode}
                 onChange={setP('postcode')}
                 placeholder="SW1A 1AA"
                 data-testid={`ulf-prop-postcode-${index}`} />
        </div>
        <div style={FIELD}>
          <span style={LBL}>Property type</span>
          <select style={INPUT} value={prop.property_type}
                  onChange={setP('property_type')}
                  data-testid={`ulf-prop-type-${index}`}>
            <option value="flat">Flat</option>
            <option value="house">House</option>
            <option value="maisonette">Maisonette</option>
            <option value="studio">Studio</option>
            <option value="hmo">HMO</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={FIELD}>
          <span style={LBL}>Bedrooms</span>
          <input style={INPUT} type="number" min={0}
                 value={prop.bedrooms}
                 onChange={setP('bedrooms')}
                 data-testid={`ulf-prop-bedrooms-${index}`} />
        </div>
      </div>

      {/* 2026-05-30 — Licensing question. Always shown so agents can
          flag HMO / selective / additional licensing on either Let or
          Available properties. Drives Compliance Scanner item 9 and is
          read by the unified backend endpoint. */}
      <div style={{ marginTop: '1rem', padding: '0.85rem',
                    border: '1px solid #e2e8f0', borderRadius: 8,
                    background: '#f8fafc' }}
           data-testid={`ulf-licensing-${index}`}>
        <div style={{ ...LBL, marginBottom: '0.5rem' }}>
          Is this property subject to mandatory HMO licensing, selective
          licensing, or additional licensing?
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button"
                  onClick={() => setProp(p => ({ ...p,
                    licensing_required: true }))}
                  data-testid={`ulf-licensing-yes-${index}`}
                  style={{
                    flex: 1, padding: '0.45rem 0.9rem',
                    border: '1px solid ' + (prop.licensing_required === true ? '#0073EA' : '#cbd5e1'),
                    background: prop.licensing_required === true ? '#0073EA' : '#fff',
                    color: prop.licensing_required === true ? '#fff' : '#0f172a',
                    borderRadius: 6, fontWeight: 600, cursor: 'pointer',
                  }}>
            Yes
          </button>
          <button type="button"
                  onClick={() => setProp(p => ({ ...p,
                    licensing_required: false, licensing_type: '',
                    licence_number: '', licence_expiry: '' }))}
                  data-testid={`ulf-licensing-no-${index}`}
                  style={{
                    flex: 1, padding: '0.45rem 0.9rem',
                    border: '1px solid ' + (prop.licensing_required === false ? '#10b981' : '#cbd5e1'),
                    background: prop.licensing_required === false ? '#10b981' : '#fff',
                    color: prop.licensing_required === false ? '#fff' : '#0f172a',
                    borderRadius: 6, fontWeight: 600, cursor: 'pointer',
                  }}>
            No
          </button>
        </div>
        {prop.licensing_required === true && (
          <div style={{ marginTop: '0.75rem', display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
            <div style={FIELD}>
              <span style={LBL}>Licence type *</span>
              <select style={INPUT} value={prop.licensing_type}
                      onChange={setP('licensing_type')}
                      data-testid={`ulf-licensing-type-${index}`}>
                <option value="">— Select —</option>
                <option value="mandatory_hmo">Mandatory HMO</option>
                <option value="selective">Selective</option>
                <option value="additional">Additional</option>
              </select>
            </div>
            <div style={FIELD}>
              <span style={LBL}>Licence number</span>
              <input style={INPUT} value={prop.licence_number}
                     onChange={setP('licence_number')}
                     data-testid={`ulf-licence-number-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Licence expiry</span>
              <input style={INPUT} type="date"
                     value={prop.licence_expiry}
                     onChange={setP('licence_expiry')}
                     data-testid={`ulf-licence-expiry-${index}`} />
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1rem' }}>
        <div style={{ ...LBL, marginBottom: '0.5rem' }}>
          Is this property currently let or available?
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button"
                  onClick={() => setStatus('occupied')}
                  data-testid={`ulf-status-let-${index}`}
                  style={{
                    flex: 1,
                    padding: '0.6rem 1rem',
                    border: '1px solid ' + (status === 'occupied' ? '#0073EA' : '#cbd5e1'),
                    background: status === 'occupied' ? '#0073EA' : '#fff',
                    color: status === 'occupied' ? '#fff' : '#0f172a',
                    borderRadius: 6, fontWeight: 600, cursor: 'pointer',
                  }}>
            Currently Let
          </button>
          <button type="button"
                  onClick={() => setStatus('available')}
                  data-testid={`ulf-status-available-${index}`}
                  style={{
                    flex: 1,
                    padding: '0.6rem 1rem',
                    border: '1px solid ' + (status === 'available' ? '#10b981' : '#cbd5e1'),
                    background: status === 'available' ? '#10b981' : '#fff',
                    color: status === 'available' ? '#fff' : '#0f172a',
                    borderRadius: 6, fontWeight: 600, cursor: 'pointer',
                  }}>
            Available to Let
          </button>
        </div>
      </div>

      {status === 'available' && (
        <div style={{ marginTop: '1rem', display: 'grid',
                      gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}
             data-testid={`ulf-available-branch-${index}`}>
          <div style={FIELD}>
            <span style={LBL}>Asking rent (£ pcm) *</span>
            <input style={INPUT} type="number" min={0}
                   value={available.asking_rent}
                   onChange={setA('asking_rent')}
                   data-testid={`ulf-asking-rent-${index}`} />
          </div>
          <div style={FIELD}>
            <span style={LBL}>Available from *</span>
            <input style={INPUT} type="date"
                   value={available.available_from}
                   onChange={setA('available_from')}
                   data-testid={`ulf-available-from-${index}`} />
          </div>
          <div style={{ ...FIELD, gridColumn: '1 / -1' }}>
            <span style={LBL}>Listing description</span>
            <textarea
              style={{ ...INPUT, minHeight: 80, fontFamily: 'inherit' }}
              value={available.listing_description}
              onChange={setA('listing_description')}
              placeholder="A bright two-bed flat with a modern kitchen and easy access to Clapham High Street…"
              data-testid={`ulf-listing-description-${index}`} />
          </div>
        </div>
      )}

      {status === 'occupied' && (
        <div data-testid={`ulf-let-branch-${index}`}>
          <div style={{ marginTop: '1rem', display: 'grid',
                        gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <div style={FIELD}>
              <span style={LBL}>Tenant first name *</span>
              <input style={INPUT} value={tenant.first_name}
                     onChange={setT('first_name')}
                     data-testid={`ulf-tenant-first-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Tenant last name *</span>
              <input style={INPUT} value={tenant.last_name}
                     onChange={setT('last_name')}
                     data-testid={`ulf-tenant-last-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Tenant email</span>
              <input style={INPUT} type="email" value={tenant.email}
                     onChange={setT('email')}
                     data-testid={`ulf-tenant-email-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Tenant phone</span>
              <input style={INPUT} value={tenant.phone}
                     onChange={setT('phone')}
                     placeholder="+44…"
                     data-testid={`ulf-tenant-phone-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Tenancy start date *</span>
              <input style={INPUT} type="date"
                     value={tenancy.start_date}
                     onChange={setTen('start_date')}
                     data-testid={`ulf-tenancy-start-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Rent amount (£) *</span>
              <input style={INPUT} type="number" min={0}
                     value={tenancy.rent_amount}
                     onChange={setTen('rent_amount')}
                     data-testid={`ulf-tenancy-rent-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Rent frequency *</span>
              <select style={INPUT} value={tenancy.rent_frequency}
                      onChange={setTen('rent_frequency')}
                      data-testid={`ulf-tenancy-freq-${index}`}>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div style={FIELD}>
              <span style={LBL}>Rent due day (1-31) *</span>
              <input style={INPUT} type="number" min={1} max={31}
                     value={tenancy.rent_due_day}
                     onChange={setTen('rent_due_day')}
                     data-testid={`ulf-tenancy-dueday-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Deposit amount (£)</span>
              <input style={INPUT} type="number" min={0}
                     value={tenancy.deposit_amount}
                     onChange={setTen('deposit_amount')}
                     data-testid={`ulf-tenancy-deposit-${index}`} />
            </div>
            <div style={FIELD}>
              <span style={LBL}>Deposit scheme</span>
              <select style={INPUT} value={tenancy.deposit_scheme}
                      onChange={setTen('deposit_scheme')}
                      data-testid={`ulf-tenancy-scheme-${index}`}>
                <option value="DPS">DPS</option>
                <option value="MyDeposits">MyDeposits</option>
                <option value="TDS">TDS</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: '1rem', display: 'grid',
                        gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <div style={{ ...FIELD, ...SECTION, padding: '1rem',
                          margin: 0, background: '#f8fafc' }}>
              <span style={LBL}>Upload Tenancy Agreement (PDF)</span>
              <input type="file" accept="application/pdf"
                     onChange={handleAgreementUpload}
                     data-testid={`ulf-tenancy-agreement-${index}`} />
              {agreementName && <span style={{ fontSize: '0.78rem', color: '#16a34a' }}>
                ✓ {agreementName}
              </span>}
            </div>
            <div style={{ ...FIELD, ...SECTION, padding: '1rem',
                          margin: 0, background: '#f8fafc' }}>
              <span style={LBL}>Upload Compliance Documents (PDF/JPG/PNG)</span>
              <input type="file" multiple
                     accept="application/pdf,image/png,image/jpeg"
                     onChange={handleComplianceUpload}
                     data-testid={`ulf-compliance-files-${index}`} />
              <span style={{ fontSize: '0.78rem', color: '#475569' }}>
                {complianceFiles.length > 0
                  ? `${complianceFiles.length} file(s) ready · AI will identify document type`
                : 'EPC, Gas Safety, EICR, Misc — drop them all together.'}
              </span>
            </div>
          </div>
        </div>
      )}

      {err && <div role="alert"
                   data-testid={`ulf-b-error-${index}`}
                   style={{ marginTop: '0.75rem', color: '#b91c1c',
                            fontSize: '0.85rem' }}>{err}</div>}

      <div style={{ marginTop: '1rem', display: 'flex',
                    justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button onClick={save} disabled={saving}
                style={BTN_PRIMARY}
                data-testid={`ulf-save-property-${index}`}>
          {saving ? 'Saving…' : 'Save Property'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wrapper
// ---------------------------------------------------------------------------
export default function UnifiedLandlordFlow({ apiBase, onClose, onSaved }) {
  const [landlord, setLandlord] = useState({
    landlord_type: 'individual',
    full_name: '', company_name: '', director_name: '',
    address_line1: '', address_line2: '', city: '', postcode: '',
    email: '', phone_number: '',
    prs_database_registered: false,
  });
  const [landlordId, setLandlordId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  // Each entry represents a Section B card. We render one for each
  // property the agent wants to add. `key` forces a fresh component
  // mount when the agent clicks "Add Another Property".
  const [propertyForms, setPropertyForms] = useState([0]);

  const saveLandlord = async () => {
    setErr(null);
    if (landlord.landlord_type === 'company') {
      if (!landlord.company_name || !landlord.director_name) {
        setErr('Company name and director name are required for a company landlord.');
        return;
      }
    } else if (!landlord.full_name) {
      setErr('Full name is required.'); return;
    }
    if (!landlord.address_line1 || !landlord.city || !landlord.postcode) {
      setErr('Address, city and postcode are required.'); return;
    }
    if (!landlord.email || !landlord.phone_number) {
      setErr('Email and phone are required.'); return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/api/landlords`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(landlord),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setErr(data.detail || 'Could not save landlord.');
        return;
      }
      setLandlordId(data._id || data.id);
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="unified-landlord-flow">
      <SectionA value={landlord}
                onChange={setLandlord}
                onSave={saveLandlord}
                saving={saving}
                err={err} />

      {landlordId && propertyForms.map((key, idx) => (
        <LandlordAddPropertyCard
          key={key}
          index={idx}
          landlordId={landlordId}
          apiBase={apiBase}
          onSaved={onSaved}
        />
      ))}

      {landlordId && (
        <div style={SECTION} data-testid="ulf-section-c">
          <h3 style={SECTION_TITLE}>C. Additional Properties</h3>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#475569',
                      marginBottom: '0.85rem' }}>
            Add as many properties as needed for this landlord. Each one
            independently goes through the Currently-Let or Available-to-Let
            flow.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setPropertyForms(p => [...p, Date.now()])}
              style={BTN_PRIMARY}
              data-testid="ulf-add-another">
              + Add Another Property for this Landlord
            </button>
            <button onClick={onClose}
                    style={BTN_GHOST}
                    data-testid="ulf-done">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
